import React, { useEffect, useRef, useState } from "react";
import { 
  SiLeetcode, 
  SiGithub, 
  SiCodechef 
} from "react-icons/si";
import { HiOutlineMail } from "react-icons/hi";


const TWO_PI = Math.PI * 2;

const makeStar = () => {
  const cr = Math.random();
  const [r,g,b] = cr>.93?[190,215,255]:cr>.86?[255,238,195]:[255,255,255];
  const fl=20+Math.floor(Math.random()*28), hl=12+Math.floor(Math.random()*45), dl=38+Math.floor(Math.random()*75), t=fl*2+hl+dl;
  return { x:Math.random(), y:Math.random(), r:Math.random()*.8+.15, pa:.35+Math.random()*.5, fs:`rgb(${r},${g},${b})`, fl,hl,dl,t, tick:-Math.floor(Math.random()*t) };
};

export default function Hero() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = "/bkg1.png";
    if (img.complete) setBgLoaded(true);
    else img.onload = () => setBgLoaded(true);
  }, []);


  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d", { alpha: true });
    let lastT=0, rz, stars=[], lmx=0, lmy=0;
    const FPS=1000/30;
    const resize=()=>{
      const dpr=window.devicePixelRatio||1, rc=canvas.getBoundingClientRect();
      canvas.width=rc.width*dpr; canvas.height=rc.height*dpr;
      ctx.setTransform(dpr,0,0,dpr,0,0);
      stars=Array.from({length:Math.min(460,Math.floor(rc.width*rc.height/4000))},makeStar);
    };
    resize();
    const onR=()=>{clearTimeout(rz);rz=setTimeout(resize,150);};
    const onM=(e)=>{
      lmx=(e.clientX/window.innerWidth-.5)*6;
      lmy=(e.clientY/window.innerHeight-.5)*6;
    };
    window.addEventListener("resize",onR,{passive:true});
    window.addEventListener("mousemove",onM);
    const draw=(ts)=>{
      animRef.current=requestAnimationFrame(draw);
      const el=ts-lastT; if(el<FPS)return; lastT=ts-(el%FPS);
      const W=canvas.width,H=canvas.height; ctx.clearRect(0,0,W,H);
      stars.forEach(s=>{
        s.tick++; const ct=((s.tick%s.t)+s.t)%s.t; let a=0;
        if(ct<s.fl)a=(ct/s.fl)*s.pa;
        else if(ct<s.fl+s.hl)a=s.pa;
        else if(ct<s.fl*2+s.hl)a=(1-(ct-s.fl-s.hl)/s.fl)*s.pa;
        if(a<.01)return;
        ctx.globalAlpha=a; ctx.fillStyle=s.fs;
        ctx.beginPath(); ctx.arc(s.x*W+lmx*s.r,s.y*H+lmy*s.r,s.r,0,TWO_PI); ctx.fill();
      });
      ctx.globalAlpha=1;
    };
    animRef.current=requestAnimationFrame(draw);
    return()=>{clearTimeout(rz);cancelAnimationFrame(animRef.current);window.removeEventListener("resize",onR);window.removeEventListener("mousemove",onM);};
  },[]);

  const linkedinPath =
    "M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.03-1.85-3.03-1.86 0-2.15 1.45-2.15 2.94v5.66H9.34V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z";

  const xPath =
    "M18.24 2h3.3l-7.2 8.23L23 22h-6.62l-5.2-6.8L5.2 22H1.9l7.7-8.8L1 2h6.78l4.7 6.2L18.24 2zm-1.16 18h1.83L7.02 3.9H5.06L17.08 20z";

  const socialLinks = [
    { type: "svg", path: linkedinPath, href: "https://www.linkedin.com/in/aryan-dwivedi14/", color: "#0A66C2", label: "LinkedIn" },
    { type: "icon", Icon: SiGithub, href: "https://github.com/Aryan457dwivedi", color: "#e2e8f0", label: "GitHub" },
    { type: "icon", Icon: SiLeetcode, href: "https://leetcode.com/u/Aryann14/", color: "#FFA116", label: "LeetCode" },
    { type: "svg", path: xPath, href: "https://x.com/aryan19687", color: "#ffffff", label: "X" },
    { type: "codeforces", href: "https://codeforces.com/profile/Aryan73", label: "Codeforces" },
    { type: "icon", Icon: SiCodechef, href: "https://www.codechef.com/users/knight_5", color: "#facc15", label: "CodeChef" },
    { type: "calendar", href: "https://cal.com/aryan-dwivedi-gjqswf/30min", label: "Calendar" },
    { type: "icon", Icon: HiOutlineMail, href: "mailto:aryandwivedi644@gmail.com", color: "#f87171", label: "Email" },
  ];

  return (
    <section
      id="home"
      className="relative h-screen flex flex-col justify-center items-center text-center overflow-hidden"
    >
      {/* Solid dark fallback */}
      <div className="absolute inset-0 bg-[#0a0a0f]" />

      {/* Animated Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
        style={{
          backgroundImage: `url('/bkg1.png')`,
          opacity: bgLoaded ? 1 : 0,
          willChange: "opacity",
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/75" />

      {/* ── Starfield canvas ── */}
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 3 }}
      />

      {/* Grid Overlay */}
      <div
        className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_85%)] animate-grid-flow"
        style={{ zIndex: 4 }}
      />
      
      {/* Glowing orbs */}
      <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[100px] animate-pulse-slow" style={{ zIndex: 2 }} />
      <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px] animate-pulse-slower" style={{ zIndex: 2 }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-[120px] animate-pulse" style={{ zIndex: 2 }} />

      

      {/* Content */}
      <div className="relative z-10 text-white max-w-4xl px-6 flex flex-col items-center gap-3" style={{ zIndex: 10 }}>
        <p className="text-sm md:text-base tracking-[0.3em] uppercase text-blue-300/90 font-light animate-fade-in">
          hello, i am
        </p>
        
        {/* Name */}
        <div className="animate-fade-in-up relative group">
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold">
            <span className="relative inline-block">
              <span className="text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.4)]
                           group-hover:drop-shadow-[0_0_40px_rgba(255,255,255,0.6)]
                           transition-all duration-500 inline-block
                           group-hover:tracking-wider">
                Aryan Dwivedi
              </span>
              <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r 
                           from-transparent via-white/60 to-transparent scale-x-0 
                           group-hover:scale-x-100 transition-transform duration-700" />
            </span>
          </h1>
        </div>
        
        {/* Role */}
        <div className="flex items-center justify-center animate-fade-in-up animation-delay-200">
          <div className="relative px-6 py-2 rounded-none bg-black 
                        border border-white/20 shadow-2xl overflow-hidden group">
            <h2 className="text-sm md:text-base text-white font-light relative z-10">
              <span className="inline-block border-r-2 border-white pr-2 animate-blink">
                Software Developer & ML Engineer
              </span>
            </h2>
          </div>
        </div>

        {/* Social Icon Bar */}
        <div className="animate-fade-in-up animation-delay-600 flex items-center justify-center gap-4 px-6 py-3 rounded-full bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-lg">
          {socialLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target={link.href.startsWith("http") ? "_blank" : undefined}
              rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
              aria-label={link.label}
              className="social-icon-link flex items-center justify-center"
              style={{ "--accent": link.color || "#ffffff" }}
            >
              {link.type === "icon" && <link.Icon size={19} />}
              {link.type === "svg" && (
                <svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor">
                  <path d={link.path} />
                </svg>
              )}
              {link.type === "codeforces" && (
                <svg viewBox="0 0 24 24" width="19" height="19">
                  <rect className="cf-bar cf-bar-1" x="0.2" y="7.8" width="6" height="16" rx="2.2" />
                  <rect className="cf-bar cf-bar-2" x="9" y="2.8" width="6" height="21.2" rx="2.2" />
                  <rect className="cf-bar cf-bar-3" x="17.8" y="10.6" width="6" height="13.4" rx="2.2" />
                </svg>
              )}
              {link.type === "calendar" && (
                <svg
                  viewBox="0 0 24 24"
                  width="19"
                  height="19"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="5" width="18" height="15" rx="2.5" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                  <line x1="7.5" y1="3" x2="7.5" y2="6.5" />
                  <line x1="16.5" y1="3" x2="16.5" y2="6.5" />
                </svg>
              )}
            </a>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-20">
        <div className="w-7 h-11 border-2 border-white/30 rounded-full flex justify-center pt-2
                      bg-white/5 backdrop-blur-md shadow-lg hover:border-white/50 
                      hover:bg-white/10 transition-all duration-500 group cursor-pointer">
          <div className="w-1.5 h-3 bg-white/70 rounded-full animate-pulse 
                        group-hover:bg-white group-hover:h-4 transition-all duration-500" />
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink {
          0%, 49%  { border-color: rgba(255,255,255,0.9); }
          50%, 100% { border-color: transparent; }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(1.1); }
        }
        @keyframes pulse-slower {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(1.15); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0; }
          10%      { opacity: 0.3; }
          50%      { transform: translateY(-100px) translateX(50px); opacity: 0.5; }
          90%      { opacity: 0.3; }
        }
        @keyframes grid-flow {
          0%   { background-position: 0 0; }
          100% { background-position: 50px 50px; }
        }
        .animate-fade-in       { animation: fade-in 0.8s ease-out; }
        .animate-fade-in-up    { animation: fade-in-up 0.8s ease-out; }
        .animation-delay-200   { animation-delay: 0.2s; animation-fill-mode: both; }
        .animation-delay-400   { animation-delay: 0.4s; animation-fill-mode: both; }
        .animation-delay-600   { animation-delay: 0.6s; animation-fill-mode: both; }
        .animate-blink         { animation: blink 1s infinite; }
        .animate-pulse-slow    { animation: pulse-slow 4s ease-in-out infinite; }
        .animate-pulse-slower  { animation: pulse-slower 6s ease-in-out infinite; }
        .animate-float         { animation: float linear infinite; }
        .animate-grid-flow     { animation: grid-flow 20s linear infinite; }

        .social-icon-link {
          color: rgba(255,255,255,0.55);
          transition: color 0.2s ease;
        }
        .social-icon-link:hover {
          color: var(--accent);
        }
        .cf-bar {
          fill: rgba(255,255,255,0.55);
          transition: fill 0.2s ease;
        }
        .social-icon-link:hover .cf-bar-1 { fill: #FCC419; }
        .social-icon-link:hover .cf-bar-2 { fill: #2F88FF; }
        .social-icon-link:hover .cf-bar-3 { fill: #F0483E; }
      `}</style>
    </section>
  );
}
